import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAccessToken } from "@/hooks";
import { ClassEnrollmentsApi } from "@/openapi/generated";
import { QueryKeys } from "@/lib/types";
import { createAuthenticatedApi } from "@/lib/utils";
import { invalidatePostedTuitionLedgerQueries } from "@/features/tuition/hooks/invalidate-posted-tuition-ledger-queries";

export type ClassForceEnrollmentCreateVariables = {
  classId: number;
  studentIds: number[];
  suppressToast?: boolean;
};

export const useClassForceEnrollmentCreate = (
  toast: ReturnType<typeof import("@/components/feedback").useToast>,
) => {
  const accessToken = useAccessToken();
  const queryClient = useQueryClient();

  const { mutate, mutateAsync, isPending, isSuccess, isError } = useMutation<
    void,
    Error,
    ClassForceEnrollmentCreateVariables
  >({
    mutationFn: async ({ classId, studentIds }) => {
      if (!accessToken) {
        throw new Error("Authentication token is required");
      }
      if (studentIds.length === 0) {
        throw new Error("At least one student is required");
      }

      const service = createAuthenticatedApi(ClassEnrollmentsApi, accessToken);
      await service.apiClassEnrollmentsForceBulkPost({
        forceClassBulkEnrollmentRequest: {
          classId,
          studentIds,
        },
      });
    },
    onSuccess: async (_data, variables) => {
      await queryClient.invalidateQueries({
        queryKey: [QueryKeys.ClassesEnrollmentsByStudentId],
      });
      await queryClient.invalidateQueries({
        queryKey: [QueryKeys.TeacherClassEnrollments],
      });
      await queryClient.invalidateQueries({
        queryKey: [QueryKeys.Class],
      });
      await queryClient.invalidateQueries({
        queryKey: [QueryKeys.Classes],
      });
      await queryClient.invalidateQueries({
        queryKey: [QueryKeys.Students],
      });
      await queryClient.invalidateQueries({
        predicate: (query) => {
          return (
            Array.isArray(query.queryKey) &&
            query.queryKey.some(
              (key) =>
                typeof key === "string" &&
                (key.includes("enrollment") || key.includes("Enrollment")),
            )
          );
        },
      });
      await invalidatePostedTuitionLedgerQueries(queryClient);

      if (variables.suppressToast) {
        return;
      }

      const count = variables.studentIds.length;
      toast({
        title: "Success",
        description:
          count === 1
            ? "Student force-enrolled in class."
            : `${count} students force-enrolled in class.`,
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    },
    onError: (error, variables) => {
      if (variables.suppressToast) {
        return;
      }

      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to force-enroll students in class";

      toast({
        title: "Error",
        description: errorMessage,
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
    },
  });

  return { mutate, mutateAsync, isPending, isError, isSuccess };
};
